import filter from 'lodash/filter';
import { computed, ref } from 'vue';

const CART_TTL_MS = 15 * 60 * 1000; // 15
const cart = {};

export function useListingCart(listingUuid) {
    if (!listingUuid) {
        throw new Error('Listing UUID is required');
    }

    const setParticipantCount = (count) => {
        cart[listingUuid].value.participantCount = count;

        cart[listingUuid].value.items.forEach((entry) => {
            if (entry.listing.qty_type === 'fixed') {
                entry.qty = count;
            }
        });

        calculateTotals();
    };

    const calculateTotals = () => {
        for (const total in cart[listingUuid].value.totals) {
            cart[listingUuid].value.totals[total] = 0;
        }
        cart[listingUuid].value.items.forEach((entry) => {
            entry.totals.shipping_total =
                entry.listing.price.shipping * entry.qty;
            entry.totals.subtotal = entry.listing.price.base * entry.qty;
            entry.totals.row_total =
                entry.totals.shipping_total + entry.totals.subtotal;
            cart[listingUuid].value.totals.subtotal += entry.totals.subtotal;
            cart[listingUuid].value.totals.shipping_total +=
                entry.totals.shipping_total;
            cart[listingUuid].value.totals.grand_total +=
                entry.totals.row_total;
        });
    };

    const addToCart = (item, qty = null) => {
        removeFromCart(item.uuid);
        cart[listingUuid].value.items.push({
            listing: item,
            qty: qty ?? cart[listingUuid].value.participantCount,
            totals: {
                subtotal: 0,
                shipping_total: 0,
                row_total: 0,
            },
        });
        calculateTotals();
    };

    const removeFromCart = (itemUuid) => {
        cart[listingUuid].value.items = filter(
            cart[listingUuid].value.items,
            (item) => {
                return item.listing.uuid !== itemUuid;
            }
        );
        calculateTotals();
    };

    const cartItemByUuid = (itemUuid) => {
        return (
            cart[listingUuid].value.items.find((item) => {
                return item.listing.uuid === itemUuid;
            }) ?? null
        );
    };

    const encodedCheckoutParameter = computed(() => {
        if (cart[listingUuid].value.items.length === 0) {
            return null;
        }
        const payload = {};
        cart[listingUuid].value.items.forEach((entry) => {
            payload[entry.listing.uuid] = entry.qty;
        });

        return window.btoa(JSON.stringify(payload));
    });

    const getStoredCart = () => {
        const storedCart = JSON.parse(
            localStorage.getItem('cart_' + listingUuid)
        );
        if (storedCart) {
            if (storedCart.meta.stored_at + CART_TTL_MS < Date.now()) {
                localStorage.removeItem('cart_' + listingUuid);
            } else {
                return storedCart;
            }
        }

        return null;
    };

    const storeCart = () => {
        if (cart[listingUuid].value.items.length === 0) {
            localStorage.removeItem('cart_' + listingUuid);
            return;
        }

        const storedCart = getStoredCart();

        const cartData = {
            meta: {
                stored_at: storedCart?.meta?.stored_at ?? Date.now(),
            },
            data: cart[listingUuid].value,
        };

        localStorage.setItem('cart_' + listingUuid, JSON.stringify(cartData));
    };

    if (!cart[listingUuid]) {
        const storedCart = getStoredCart();
        if (storedCart) {
            cart[listingUuid] = ref(storedCart.data);
        } else {
            cart[listingUuid] = ref({
                totals: {
                    subtotal: 0,
                    shipping_total: 0,
                    grand_total: 0,
                },
                participantCount: 0,
                items: [],
            });
        }
    }

    window.onbeforeunload = () => {
        storeCart();
    };

    return {
        cart: cart[listingUuid],
        addToCart,
        cartItemByUuid,
        encodedCheckoutParameter,
        removeFromCart,
        setParticipantCount,
    };
}
